import {Grid, Typography} from "@mui/material";
import {useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {FormAlertModel} from "../../../api/types/alert";
import {FormAutocomplete, FormTextField} from "../../../common/form-fields";
import {setFormNumberValue} from "../../../utils";
import {numberIntegerRegex} from "../../../constants";
import React, {useContext} from "react";
import DealerAndTenant from "../../../common/components/DealerAndTenant";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {SelectElementModelBE, SelectElementModelFE} from "../../../api/types/generic";

type AlertFormProps = {
    readonly: boolean;
};

const CompetitorsBenchmarksForm = ({readonly}: AlertFormProps) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    //USE FORM CONTEXT
    const {register, setValue, formState} = useFormContext<FormAlertModel>();

    return (
        <Grid container columnSpacing={2}>
            {/* DESCRIPTION/NAME */}
            <Typography variant="h6" width={"100%"} pl={2}>
                {t("pricing:alert.labels.descriptionTitle")}
            </Typography>
            <Grid item xs={12} md={3}>
                <FormTextField
                    {...register("description")}
                    label={`${t("pricing:alert.labels.description")}*`}
                    readOnly={readonly}
                />
            </Grid>
            {/* DEALER/TENANT */}
            {legalEntityOptions.length === 1 && dealerOption.length === 1 ? null :
                <Typography variant="h6" width={"100%"} pl={2} mt={4}>
                    Tenant
                </Typography>
            }
            <Grid item xs={12} md={6}>
                <DealerAndTenant
                    readOnly={readonly}
                    md={6}
                    legalEntityName={"legalEntity"}
                    dealerName={"dealer"}
                />
            </Grid>
            {/* N-esima posizione */}
            <Typography variant="h6" width={"100%"} pl={2} mt={4}>
                {t("pricing:alert.labels.thresholdParameters")}
            </Typography>
            <Grid item xs={12} md={3}>
                <FormTextField
                    {...register("competitorsBenchmarks.topPricePosition")}
                    label={`${t("pricing:alert.labels.topPricePosition")}*`}
                    readOnly={readonly}
                    type={"text"}
                    handleChange={(value: string) => {
                        setFormNumberValue(
                            value,
                            setValue,
                            "competitorsBenchmarks.topPricePosition",
                            numberIntegerRegex
                        );
                        if (numberIntegerRegex.test(value.trim())) {
                            const rule = `${t(
                                "pricing:alert.labels.rules.competitorsBenchmarks.firstPartRules"
                            )}${Number(value)}`;
                            setValue("competitorsBenchmarks.firstRule", rule);
                        }
                    }}
                />
            </Grid>
            {/* M-esima posizione (dal fondo) */}
            <Grid item xs={12} md={3}>
                <FormTextField
                    {...register("competitorsBenchmarks.bottomPricePosition")}
                    label={`${t("pricing:alert.labels.bottomPricePosition")}*`}
                    readOnly={readonly}
                    type={"text"}
                    handleChange={(value: string) => {
                        setFormNumberValue(
                            value,
                            setValue,
                            "competitorsBenchmarks.bottomPricePosition",
                            numberIntegerRegex
                        );
                        if (numberIntegerRegex.test(value.trim())) {
                            const rule = `${t(
                                "pricing:alert.labels.rules.competitorsBenchmarks.firstPartRules"
                            )}${Number(value)}`;
                            setValue("competitorsBenchmarks.secondRule", rule);
                        }
                    }}
                />
            </Grid>
            {/* REGOLA NUMERO 1 */}
            <Typography variant="h6" width={"100%"} pl={2} mt={4}>
                {t("pricing:alert.labels.rules.label")}
            </Typography>
            <Grid item xs={12} md={3}>
                <FormTextField
                    {...register("competitorsBenchmarks.firstRule")}
                    readOnly={true}
                />
            </Grid>
            {/* REGOLA NUMERO 2 */}
            <Grid item xs={12} md={3}>
                <FormTextField
                    {...register("competitorsBenchmarks.secondRule")}
                    readOnly={true}
                />
            </Grid>
            {/* DESTINATARI/EMAIL */}
            <Typography variant="h6" width={"100%"} pl={2} mt={4}>
                {t("pricing:alert.labels.receiverTitle")}
            </Typography>
            <Grid item xs={12} md={6}>
                <FormAutocomplete
                    {...register("recipientLists")}
                    options={[]}
                    freeSolo={true}
                    multiple={true}
                    label={`${t("pricing:alert.labels.receiver")}*`}
                    helperText={
                        formState.errors.recipientLists
                            ? formState.errors.recipientLists[0]?.message
                            : ""
                    }
                    readOnly={readonly}
                />
            </Grid>
        </Grid>
    );
};

export default CompetitorsBenchmarksForm;
